import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import { IContentCard } from '@models/IContentCard';
import truncateText from '@utils/helpers/truncateText';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

interface Props extends IContentCard {
  readonly largeCard?: boolean;
  readonly dataTestId?: string;
  readonly showMoreLabel: string;
  readonly showLessLabel: string;
}

const ContentCard = ({
  image,
  title,
  link,
  text,
  largeCard,
  dataTestId = 'CT-74-Content-Card',
  showMoreLabel,
  showLessLabel,
}: Props) => {
  const isMobile = useMediaQuery(1024);
  const isBelowXXLScreen = useMediaQuery(1920);

  const [truncatedText, setTruncatedText] = useState('');

  // Replicated the line heights for body-two class, in order to properly compute the cutoff height
  const textLineHeight = (() => {
    if (isMobile) return 22;
    if (isBelowXXLScreen) return 26;
    return 28;
  })();

  const [textRef, { scrollHeight: textContentHeight }] = useResizeObserver();
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const textCutOff = largeCard ? 4 * textLineHeight : 5 * textLineHeight;
  const isTextExpandable = textContentHeight > textCutOff;
  const hasCTALink = link?.url && link?.name;

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    const formattedText = text.replace(/<\/?p>/g, '').replace(/\n+/g, ' ');
    setTruncatedText(truncateText(formattedText, 300, '...'));
  }, []);

  return (
    <div className="flex h-full flex-col gap-4 lg:gap-5">
      <div className="w-full pb-4">
        <Image
          image={image}
          imageClasses="w-full h-full object-cover left-0 -z-1 aspect-video"
          dataTestId={`${dataTestId}-Image`}
        />
      </div>
      <div className="flex w-full flex-col gap-4 md:w-[90%] lg:gap-5">
        <p
          className={`${largeCard ? 'h3' : 'h4'} dark:text-white`}
          data-testid={`${dataTestId}-Title`}
        >
          {title}
        </p>

        <div className="flex flex-col items-start">
          <motion.div
            ref={textRef}
            className="body-two overflow-hidden dark:text-grey-500"
            {...(hasCTALink
              ? {}
              : {
                  initial: { height: 0 },
                  animate: isTextExpanded ? 'expanded' : 'collapsed',
                  variants: {
                    expanded: { height: 'auto' },
                    collapsed: {
                      height: isTextExpandable ? textCutOff : 'auto',
                    },
                  },
                })}
            dangerouslySetInnerHTML={{
              __html: text?.length > 300 && hasCTALink ? truncatedText : text,
            }}
            data-testid={`${dataTestId}-Text`}
          />

          {isTextExpandable && !hasCTALink && (
            <button
              className="shrink-0 underline dark:text-grey-500"
              onClick={() => {
                setIsTextExpanded(!isTextExpanded);
              }}
              data-testid={`${dataTestId}-Show-More/Less`}
            >
              <strong>{isTextExpanded ? showLessLabel : showMoreLabel}</strong>
            </button>
          )}
        </div>

        {hasCTALink && (
          <div>
            <Button link={link} dataTestId={`${dataTestId}-Cta`} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentCard;
